import type { StatusDotProps } from "@/components/ui/StatusDot";
import { FilterType } from "@/constants/filter";
import type { AnyLabeledFilterConfig } from "@/types/list";
import { formatDateOnly, fromLocalCalendarDate } from "@/utils/date-only";
import { getFilterExclusions } from "@/utils/negative-list-filtering";
import { isDate } from "date-fns";
import type { ReactNode } from "react";

export type GenericFilterValue = unknown;
export type GenericFilterValues = Record<string, unknown>;
export type FilterOptionLabelCache = Record<string, Record<string, string>>;
export type FilterValueOption = {
  value: string;
  label: string;
  icon?: ReactNode;
  tone?: StatusDotProps["tone"];
};

export const filterOptionLabelCacheInputKey = "__filterOptionLabelCache";

function truncateFilterValue(value: string, maxLength = 24) {
  return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;
}

const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;

function humanizeFallbackValue(value: string) {
  if (!value.trim()) {
    return value;
  }

  if (uuidPattern.test(value)) {
    return "Selected option";
  }

  if (!/[A-Z_]/.test(value)) {
    return value;
  }

  return value
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function resolveFilterProps(
  filterConfig: AnyLabeledFilterConfig,
  filterInput: Record<string, unknown>,
) {
  return typeof filterConfig.props === "function"
    ? filterConfig.props(filterInput)
    : filterConfig.props;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function getFilterOptionLabelCache(
  filterInput: Record<string, unknown>,
): FilterOptionLabelCache | undefined {
  const cache = filterInput[filterOptionLabelCacheInputKey];

  if (!isRecord(cache)) {
    return undefined;
  }

  return cache as FilterOptionLabelCache;
}

function getFilterLabelCacheOptions(
  filterInput: Record<string, unknown>,
  filterKey: string | undefined,
) {
  if (!filterKey) {
    return [];
  }

  const cache = getFilterOptionLabelCache(filterInput);
  const filterCache = cache?.[filterKey];

  if (!filterCache) {
    return [];
  }

  return Object.entries(filterCache).map(([value, label]) => ({
    value,
    label,
  }));
}

function collectFilterOptions(options: unknown): FilterValueOption[] {
  if (!Array.isArray(options)) {
    return [];
  }

  return options.flatMap((option) => {
    if (!isRecord(option)) {
      return [];
    }

    if (!("value" in option) || !("label" in option)) {
      return [];
    }

    return [{
      value: String(option.value),
      label: String(option.label),
      ...(option.icon == null ? {} : { icon: option.icon as ReactNode }),
      ...(typeof option.tone === "string" ? { tone: option.tone as StatusDotProps["tone"] } : {}),
    }];
  });
}

function getResolvedFilterOptions(
  filterConfig: AnyLabeledFilterConfig,
  filterInput: Record<string, unknown>,
  filterKey?: string,
) {
  const resolvedProps = resolveFilterProps(filterConfig, filterInput);
  const resolvedPropRecord = isRecord(resolvedProps) ? resolvedProps : {};
  const options = [
    ...collectFilterOptions(filterConfig.options),
    ...collectFilterOptions(resolvedPropRecord.options),
    ...collectFilterOptions(resolvedPropRecord.defaultOptions),
    ...getFilterLabelCacheOptions(filterInput, filterKey),
  ];
  const optionMap = new Map<string, FilterValueOption>();

  options.forEach((option) => {
    optionMap.set(option.value, { ...optionMap.get(option.value), ...option });
  });

  return Array.from(optionMap.values());
}

/** The option definition behind one filter value, with any icon or status tone it carries. */
export function getFilterValueOption(
  filterConfig: AnyLabeledFilterConfig,
  rawValue: string,
  filterInput: Record<string, unknown>,
  filterKey?: string,
) {
  return getResolvedFilterOptions(filterConfig, filterInput, filterKey).find(
    (option) => option.value === rawValue,
  );
}

function resolveFilterOptionLabel(
  filterConfig: AnyLabeledFilterConfig,
  rawValue: string,
  filterInput: Record<string, unknown>,
  filterKey?: string,
) {
  return getFilterValueOption(filterConfig, rawValue, filterInput, filterKey)?.label ?? humanizeFallbackValue(rawValue);
}

const academicScopeValuePattern = /^(?:season|seasonIntake|programOffering):/u;

/** A Season, intake or program value whose name has not resolved yet; its only text is the raw scope ID. */
export function isPendingAcademicScopeValue(
  filterConfig: AnyLabeledFilterConfig,
  value: unknown,
  filterInput: Record<string, unknown>,
  filterKey?: string,
) {
  return typeof value === "string"
    && academicScopeValuePattern.test(value)
    && !getResolvedFilterOptions(filterConfig, filterInput, filterKey).some(option => option.value === value);
}

export function isFilterValueActive(
  filterConfig: AnyLabeledFilterConfig,
  value: GenericFilterValue,
) {
  if (value == null) {
    return false;
  }

  if (typeof value === "boolean") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (filterConfig.type === FilterType.DATE && isDate(value)) {
    return true;
  }

  if (typeof value === "string") {
    return value.trim().length > 0;
  }

  return true;
}

export function getActiveConfiguredFilterKeys(
  filterConfigs: Record<string, AnyLabeledFilterConfig>,
  currentValues: GenericFilterValues | undefined,
  hiddenFilterKeys: string[] = [],
) {
  if (!currentValues) {
    return [];
  }

  const hiddenFilterKeySet = new Set(hiddenFilterKeys);

  return Object.entries(filterConfigs).flatMap(([filterKey, filterConfig]) => {
    if (hiddenFilterKeySet.has(filterKey)) {
      return [];
    }

    return (isFilterValueActive(filterConfig, currentValues[filterKey])
        || isFilterValueActive(filterConfig, getFilterExclusions(currentValues)[filterKey]))
      ? [filterKey]
      : [];
  });
}

export function hasActiveConfiguredFilterValues(
  filterConfigs: Record<string, AnyLabeledFilterConfig>,
  currentValues: GenericFilterValues | undefined,
  hiddenFilterKeys: string[] = [],
) {
  return getActiveConfiguredFilterKeys(
    filterConfigs,
    currentValues,
    hiddenFilterKeys,
  ).length > 0;
}

export function mergeFilterValues(
  currentValues: GenericFilterValues | undefined,
  formValues: GenericFilterValues,
  name: keyof GenericFilterValues,
  value: GenericFilterValue,
) {
  const mergedValues: GenericFilterValues = {
    ...(currentValues ?? {}),
    ...formValues,
  };

  if (
    value == null
    || value === false
    || (typeof value === "string" && value.trim().length === 0)
    || (Array.isArray(value) && value.length === 0)
  ) {
    if (name === "season" || name === "seasonScopeValues") mergedValues[name] = [];
    else delete mergedValues[String(name)];
    return mergedValues;
  }

  mergedValues[String(name)] = value;
  return mergedValues;
}

export function formatFilterValueLabel(
  filterConfig: AnyLabeledFilterConfig,
  value: GenericFilterValue,
  filterInput: Record<string, unknown>,
  filterKey?: string,
) {
  if (value == null) {
    return "";
  }

  if (filterConfig.type === FilterType.DATE && isDate(value)) {
    return formatDateOnly(fromLocalCalendarDate(value));
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "";
  }

  if (Array.isArray(value)) {
    const resolvedLabels = value.map((item) => resolveFilterOptionLabel(filterConfig, String(item), filterInput, filterKey));
    const labels = filterKey === "season" ? [...new Set(resolvedLabels)] : resolvedLabels;

    if (labels.length <= 2) {
      return labels.join(", ");
    }

    return `${labels[0]} +${labels.length - 1}`;
  }

  if (typeof value === "string") {
    if (filterConfig.type === FilterType.SELECT || filterConfig.type === FilterType.MULTI_SELECT) {
      return truncateFilterValue(
        resolveFilterOptionLabel(filterConfig, value, filterInput, filterKey),
      );
    }

    return truncateFilterValue(value);
  }

  return truncateFilterValue(String(value));
}

export function getFilterValueLabels(
  filterConfig: AnyLabeledFilterConfig,
  value: GenericFilterValue,
  filterInput: Record<string, unknown>,
  filterKey?: string,
) {
  if (value == null) {
    return [];
  }

  if (filterConfig.type === FilterType.DATE && isDate(value)) {
    return [formatDateOnly(fromLocalCalendarDate(value))];
  }

  if (typeof value === "boolean") {
    return value ? ["Yes"] : [];
  }

  if (Array.isArray(value)) {
    return value.map((item) => {
      const label = resolveFilterOptionLabel(filterConfig, String(item), filterInput, filterKey);
      return ["placementRequirementMetric", "todoRequirements", "reviewRequirements", "renewalRequirements"].includes(filterKey ?? "") ? label : truncateFilterValue(label);
    });
  }

  return [formatFilterValueLabel(filterConfig, value, filterInput, filterKey)].filter(Boolean);
}
